import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { connect } from 'react-redux'
import { useForm } from 'react-hook-form'
import classnames from 'classnames'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import useDeferredCaptchaSubmit from 'hooks/useDeferredCaptchaSubmit'
import { dispatchLogin } from 'reduxModules/auth/dispatchers'
import { loginBoxSelector } from 'reduxModules/app/selectors'
import { OTP_LOGIN_PATH, otpLoginState } from '../../../utils/otpRoute'
import { CaptchaNotice } from '../CaptchaNotice'
import keyUpHandler from '../../utils/keyUpHandler'
import FacebookSignIn from '../../generic/FacebookSignIn/index'
import GoogleSignIn from '../../generic/GoogleSignIn/index'
import AppleSignIn from '../../generic/AppleSignIn/index'
import OpenAthensSignIn from '../../generic/OpenAthensSignIn/index'
import CaptchaStandalone from '../../CaptchaStandalone/_lazy'
import EMAIL_PATTERN from '../../utils/emailPattern'
import InlineError, { ariaErrorProps } from '../InlineError'
import '../RenderedField/styles.css'
import './styles.css'


export function LoginBox({
  autoFocus = false,
  inCheckout = false,
  isMain = false,
  toggleAuth,
  location,
  recaptcha,
  onSubmit,
  next
}) {
  const [passwordVisible, setPasswordVisible] = useState(false)
  const routeLocation = useLocation()
  const navigate = useNavigate()
  // mode onChange validates as the user types (so the format error shows even
  // when the field is autofocused and never blurred) instead of waiting for
  // submit
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty }
  } = useForm({ mode: 'onChange' })
  const email = watch('email')

  // SPEC-041: the one-time code login page, reached either from the option in
  // the sign-in stack (with whatever valid email is typed so far) or straight
  // after a rejected password (FR-3). The flow travels as route state.
  const otpState = (address) =>
    otpLoginState({
      email: address,
      trigger: 'login',
      inCheckout,
      location: routeLocation
    })

  const submitLogin = (data) =>
    Promise.resolve(onSubmit(data)).then((result) => {
      if (result?.offerOtp)
        navigate(OTP_LOGIN_PATH, { state: otpState(data.email) })
    })

  // Gate submission on Turnstile: capture an early click and replay it once the
  // token lands, and keep the form locked until the submission settles.
  const { submit, awaitingVerification, isSubmitting } = useDeferredCaptchaSubmit(
    recaptcha.token,
    submitLogin
  )

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible)
  }

  const isSubmitDisabled = !isDirty || isSubmitting || awaitingVerification

  return (
    <div id="LoginBox" role={isMain ? 'main' : 'region'}>
      <div className="sign-in-box auth-form-box">
        {inCheckout && (
          <button
            className="toggle-box"
            onClick={() => toggleAuth(true)}
          >
            Create your account
          </button>
        )}
        <form id="SignInForm" onSubmit={handleSubmit(submit)} noValidate>
          <div className={classnames('field', '-floating', { '-error': errors.email })}>
            <div className="field-container">
              <input
                {...register('email', {
                  required: 'This field is required.',
                  pattern: {
                    value: EMAIL_PATTERN,
                    message: 'Invalid email address.'
                  }
                })}
                id="login-email"
                type="email"
                placeholder=" "
                autoFocus={autoFocus}
                {...ariaErrorProps('login-email', errors.email)}
              />
              <label htmlFor="login-email">Email</label>
              <InlineError id="login-email" message={errors.email?.message} />
            </div>
          </div>
          <div className={classnames('field', '-floating', { '-error': errors.password })}>
            <div className="field-container">
              <input
                {...register('password', {
                  required: 'This field is required.'
                })}
                id="login-password"
                type={passwordVisible ? 'text' : 'password'}
                placeholder=" "
                {...ariaErrorProps('login-password', errors.password)}
              />
              <label htmlFor="login-password">Password</label>
              <i
                className={classnames('icon', passwordVisible ? 'icon-passcodewatch' : 'icon-passcodewatch disabled')}
                aria-label="Toggle password visibility"
                onClick={togglePasswordVisibility}
                onKeyUp={event => keyUpHandler(event, togglePasswordVisibility)}
                tabIndex="0"
              />
              <InlineError
                id="login-password"
                message={errors.password?.message}
              />
            </div>
          </div>
          <CaptchaStandalone />
          <button
            type="submit"
            className="btn -wide -blue-2"
            disabled={isSubmitDisabled}
            style={{ width: '100%' }}
          >
            {awaitingVerification ? 'Verifying…' : 'Login'}
          </button>
        </form>

        <div className="divisor">or</div>
        <div className="social-logins">
          <Link
            className="btn -wide otp-sso"
            to={OTP_LOGIN_PATH}
            state={otpState(errors.email ? '' : email || '')}
          >
            <svg
              className="otp-sso-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path
                fill="currentColor"
                d="M3 5h18a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1zm1 2v.6l8 5.2 8-5.2V7H4zm16 3-8 5.2L4 10v7h16v-7z"
              />
            </svg>
            Continue with email code
          </Link>
          <GoogleSignIn
            text="Google Login"
            location={location}
            inCheckout={inCheckout}
          />
          <AppleSignIn
            text="Apple Login"
            location={location}
            inCheckout={inCheckout}
          />
          <FacebookSignIn
            text="Facebook Login"
            location={location}
            inCheckout={inCheckout}
          />
          <OpenAthensSignIn />
        </div>
        {!inCheckout && (
          <div className="auth-alt">
            or <Link to="/users/signup">Register</Link> <br /> to create an account
          </div>
        )}

        <div className="forgot-password">
          <Link to="/users/password/new">Forgot password?</Link>
        </div>
        <CaptchaNotice dark />
      </div>
    </div>
  )
}

LoginBox.propTypes = {
  autoFocus: PropTypes.bool,
  inCheckout: PropTypes.bool,
  isMain: PropTypes.bool,
  toggleAuth: PropTypes.func,
  location: PropTypes.object,
  recaptcha: PropTypes.shape({
    token: PropTypes.string,
    version: PropTypes.string
  }),
  onSubmit: PropTypes.func.isRequired,
  next: PropTypes.string
}

export default connect(
  loginBoxSelector,
  null,
  (stateProps, dispatchProps, ownProps) => {
    const { dispatch } = dispatchProps
    return {
      ...stateProps,
      ...ownProps,
      onSubmit: (data) =>
        dispatchLogin(
          dispatch,
          { ...data, recaptcha: stateProps.recaptcha },
          ownProps
        )
    }
  }
)(LoginBox)
